"use client";

import { motion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  Cloud,
  FileSpreadsheet,
  MonitorSmartphone,
  Users,
} from "lucide-react";

import Section from "./ui/Section";
import ProductShowcase from "./showcase/ProductShowcase";
import GradientButton from "./ui/GradientButton";

const features = [
  {
    icon: FileSpreadsheet,
    title: "Exam Creation & Scheduling",
    description:
      "Build objective assessments with configurable questions, timing, and scoring settings.",
  },
  {
    icon: MonitorSmartphone,
    title: "Exam Monitoring",
    description:
      "Follow supported live exam activity and review available student progress information.",
  },
  {
    icon: BarChart3,
    title: "Performance Reports",
    description:
      "Review available results and compare performance across students, batches, and exams.",
  },
  {
    icon: Users,
    title: "Institute & Student Management",
    description:
      "Organize institute users and teaching workflows through the platform's available tools.",
  },
  {
    icon: Cloud,
    title: "Web-Based Access",
    description:
      "Use the platform through a browser without maintaining your own exam-server infrastructure.",
  },
];

export default function InstituteExperience() {
  return (
    <Section className="relative overflow-hidden bg-[#050B1A] py-20 lg:py-28">
      {/* Premium background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-48 top-1/4 h-[32rem] w-[32rem] rounded-full bg-brand-gold/[0.07] blur-[120px]" />
        <div className="absolute -right-48 bottom-[-8rem] h-[34rem] w-[34rem] rounded-full bg-blue-500/[0.07] blur-[130px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/25 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      <div className="relative z-10 grid items-center gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:gap-16">
        {/* Institute capabilities */}
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="min-w-0"
        >
          <div className="inline-flex items-center rounded-full border border-brand-gold/30 bg-brand-gold/[0.05] px-3.5 py-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-brand-gold">
              For Schools & Institutes
            </span>
          </div>

          <h2 className="mt-6 max-w-xl text-4xl font-black leading-[1.06] tracking-tight text-white md:text-5xl xl:text-[3.35rem]">
            A smarter way to
            <br className="hidden sm:block" />
            <span className="text-brand-gold">manage assessments.</span>
          </h2>

          <p className="mt-5 max-w-lg text-base leading-7 text-white/60 md:text-lg md:leading-8">
            Create and publish exams, organize student activity, and review
            performance from one web-based platform built for institute
            assessment workflows.
          </p>

          <div className="mt-8 space-y-3">
            {features.map((feature, index) => {
              const Icon = feature.icon;

              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.35, delay: index * 0.05 }}
                  whileHover={{ y: -2 }}
                  className="group flex items-start gap-3.5 rounded-2xl border border-white/10 bg-white/[0.035] p-4 transition-colors duration-300 hover:border-brand-gold/25 hover:bg-white/[0.055] sm:gap-4"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-gold/25 bg-brand-gold/[0.06] text-brand-gold transition-colors group-hover:bg-brand-gold/[0.12]">
                    <Icon className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold tracking-tight text-white sm:text-[15px]">
                      {feature.title}
                    </h3>
                    <p className="mt-1 text-xs leading-5 text-white/50 sm:text-sm sm:leading-6">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-8 flex flex-col gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/35">
                Built for institute workflows
              </p>
              <p className="mt-1 text-sm leading-5 text-white/50">
                Explore the platform and its available tools.
              </p>
            </div>
            <GradientButton
              href="/institutes"
              className="shrink-0 whitespace-nowrap"
            >
              Explore Platform
              <ArrowRight className="ml-2 h-4 w-4" />
            </GradientButton>
          </motion.div>
        </motion.div>

        {/* Analytics showcase */}
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.05 }}
          className="relative min-w-0"
        >
          <div className="pointer-events-none absolute -inset-8 rounded-[3rem] bg-brand-gold/[0.045] blur-[100px]" />
          <div className="relative">
            <ProductShowcase mode="analytics" />
          </div>
          <p className="mt-3 text-center text-[11px] leading-5 text-white/35">
            Product preview for illustration. Available tools and data depend
            on account access and platform configuration.
          </p>
        </motion.div>
      </div>
    </Section>
  );
}
